<template>
  <b-modal
    id="posterEditor"
    size="lg"
    scrollable
    hide-footer
    :title="t('posterEdit')"
    title-class="poster-editor-title"
    :dir="isHebrew ? 'rtl' : 'ltr'"
  >
    <div class="poster-editor">
      <!-- Design -->
      <section class="editor-section">
        <h4 class="editor-title">{{ t('posterEditDesign') }}</h4>
        <div class="editor-designs">
          <button
            v-for="option in designs"
            :key="option.id"
            type="button"
            :class="['editor-design', { 'is-picked': draft.design === option.id }]"
            :aria-pressed="draft.design === option.id ? 'true' : 'false'"
            @click="draft.design = option.id"
          >
            <component :is="option.preview"/>
            <span class="editor-design-name">{{ t(option.nameKey) }}</span>
          </button>
        </div>
      </section>

      <template v-if="draft.design === 'poster'">
        <!-- Which days each stop covers -->
        <section class="editor-section">
          <h4 class="editor-title">{{ t('posterEditDays') }}</h4>
          <p class="editor-hint">{{ t('posterEditDaysHint') }}</p>
          <ol class="editor-days">
            <li
              v-for="(name, index) in dayNames"
              :key="index"
              :class="['editor-day', { 'starts-stop': startsStop(index + 1) }]"
            >
              <span v-if="startsStop(index + 1)" class="editor-stop-tag">
                {{ t('posterEditStop', { number: stopNumber(index + 1) }) }}
              </span>
              <label class="editor-day-row">
                <input
                  type="checkbox"
                  :checked="startsStop(index + 1)"
                  :disabled="index === 0"
                  @change="toggleSplit(index + 1)"
                >
                <span class="editor-day-label">{{ t('dayNumber', { number: index + 1 }) }}</span>
                <span class="editor-day-name">{{ name }}</span>
                <span class="editor-day-new">{{ t('posterEditNewStop') }}</span>
              </label>
            </li>
          </ol>
        </section>

        <!-- A photo for each stop -->
        <section class="editor-section">
          <h4 class="editor-title">{{ t('posterEditPhotos') }}</h4>
          <div v-for="(stop, index) in previewStops" :key="stop.firstDay" class="editor-stop">
            <div class="editor-stop-head">
              <span class="editor-stop-number">{{ index + 1 }}</span>
              <!-- Empty: the automatic name, shown as the placeholder. -->
              <input
                type="text"
                class="editor-stop-name"
                :value="draft.names[stop.firstDay] || ''"
                :placeholder="automaticNames[stop.firstDay]"
                :maxlength="nameMaxLength"
                :aria-label="t('posterEditStopName')"
                @input="setName(stop, $event.target.value)"
              >
              <span class="editor-stop-days">{{ dayRange(stop) }}</span>
            </div>
            <div class="editor-photos">
              <button
                type="button"
                :class="['editor-photo', 'editor-photo-auto', { 'is-picked': !draft.photos[stop.firstDay] }]"
                @click="pickPhoto(stop, null)"
              >
                <img
                  v-if="automaticPhotos[stop.firstDay]"
                  :src="$store.getters.getResizeImageForMobileDevices(automaticPhotos[stop.firstDay])"
                  alt=""
                  loading="lazy"
                  @error="useFullImage($event, automaticPhotos[stop.firstDay])"
                >
                <span class="editor-photo-auto-label">{{ t('posterEditPhotoAuto') }}</span>
              </button>
              <button
                v-for="option in photoOptions(stop)"
                :key="option.url"
                type="button"
                :title="option.label"
                :class="['editor-photo', { 'is-picked': draft.photos[stop.firstDay] === option.url }]"
                @click="pickPhoto(stop, option.url)"
              >
                <img
                  :src="$store.getters.getResizeImageForMobileDevices(option.url)"
                  :alt="option.label"
                  loading="lazy"
                  @error="useFullImage($event, option.url)"
                >
              </button>
            </div>
            <p v-if="!photoOptions(stop).length" class="editor-hint">{{ t('posterEditNoPhotos') }}</p>
          </div>
        </section>
      </template>

      <div class="editor-footer">
        <button type="button" class="editor-btn editor-btn-quiet" @click="resetToAutomatic">
          {{ t('posterEditReset') }}
        </button>
        <span v-if="failed" class="editor-error">{{ t('posterEditSaveFailed') }}</span>
        <div class="editor-footer-actions">
          <button type="button" class="editor-btn" @click="close">{{ t('posterEditCancel') }}</button>
          <button type="button" class="editor-btn editor-btn-primary" :disabled="saving" @click="save">
            {{ saving ? t('posterEditSaving') : t('posterEditSave') }}
          </button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { translationMixin } from "@/mixins/translationMixin";
import { DESIGN_LIST } from "@/designs";
import {
  buildPosterStops, posterDayNames, posterPhotoOptions, POSTER_SETTINGS_VERSION,
  POSTER_NAME_MAX_LENGTH,
} from "@/utils/posterStops";
import { designSettings } from "@/utils/rideDesigns";

/**
 * The ride manager's poster editor: the trip page's design, which days each
 * poster stop covers, and the photo of each stop, picked from that stop's
 * hotel and places (the same kind of choice as the PDF's photo picker).
 *
 * Works on a draft and saves it all at once (saveRidePresentation): the
 * splits only when the manager changed them, the photos only for days that
 * still start a stop. "Back to automatic" clears both.
 *
 * Open with this.$refs.<ref>.open().
 */
export default {
  name: 'PosterEditor',
  mixins: [translationMixin],
  props: {
    ride: { type: Object, required: true },
    // allRide.attractionsByDay
    days: { type: Array, default: () => [] },
  },
  data() {
    return {
      designs: DESIGN_LIST,
      draft: { design: 'poster', splits: [1], splitsEdited: false, photos: {}, names: {} },
      nameMaxLength: POSTER_NAME_MAX_LENGTH,
      saving: false,
      failed: false,
    };
  },
  computed: {
    dayNames() {
      return posterDayNames(this.days);
    },
    draftSettings() {
      const photos = {};
      const starts = this.draft.splits;
      Object.keys(this.draft.photos).forEach(day => {
        if (this.draft.photos[day] && starts.includes(Number(day))) photos[day] = this.draft.photos[day];
      });
      const names = {};
      Object.keys(this.draft.names).forEach(day => {
        const name = (this.draft.names[day] || '').trim();
        if (name && starts.includes(Number(day))) names[day] = name;
      });
      const settings = {};
      if (this.draft.splitsEdited) settings.splits = starts.slice();
      if (Object.keys(photos).length) settings.photos = photos;
      if (Object.keys(names).length) settings.names = names;
      // "v": the shape this editor saves, so a newer backend can upgrade it.
      return Object.keys(settings).length ? Object.assign({ v: POSTER_SETTINGS_VERSION }, settings) : null;
    },
    // What each stop shows when the manager picks nothing: the "automatic" tile.
    automaticPhotos() {
      const photos = {};
      buildPosterStops(this.days, { settings: { splits: this.draft.splits } })
        .forEach(stop => { photos[stop.firstDay] = stop.photo; });
      return photos;
    },
    // What each stop is called when the manager types nothing: the name box's
    // placeholder.
    automaticNames() {
      const names = {};
      buildPosterStops(this.days, { settings: { splits: this.draft.splits } })
        .forEach(stop => { names[stop.firstDay] = stop.name; });
      return names;
    },
    // The poster as it will look, with the draft's splits.
    previewStops() {
      return buildPosterStops(this.days, {
        settings: { splits: this.draft.splits, photos: this.draft.photos },
      });
    },
  },
  methods: {
    open() {
      const saved = designSettings(this.ride, 'poster', POSTER_SETTINGS_VERSION);
      const current = buildPosterStops(this.days, { settings: saved });
      this.draft = {
        design: this.ride.presentation_design || 'poster',
        splits: current.map(stop => stop.firstDay),
        splitsEdited: !!(saved && saved.splits),
        photos: Object.assign({}, (saved && saved.photos) || {}),
        names: Object.assign({}, (saved && saved.names) || {}),
      };
      this.failed = false;
      this.$bvModal.show('posterEditor');
    },
    close() {
      this.$bvModal.hide('posterEditor');
    },
    startsStop(day) {
      return this.draft.splits.includes(day);
    },
    stopNumber(day) {
      return this.draft.splits.indexOf(day) + 1;
    },
    toggleSplit(day) {
      if (day === 1) return;
      const splits = this.startsStop(day)
        ? this.draft.splits.filter(d => d !== day)
        : this.draft.splits.concat(day).sort((a, b) => a - b);
      this.draft = Object.assign({}, this.draft, { splits, splitsEdited: true });
    },
    dayRange(stop) {
      return stop.firstDay === stop.lastDay
        ? this.t('dayNumber', { number: stop.firstDay })
        : this.t('posterDayRange', { from: stop.firstDay, to: stop.lastDay });
    },
    photoOptions(stop) {
      return posterPhotoOptions(this.days, stop.firstDay, stop.lastDay);
    },
    pickPhoto(stop, url) {
      const photos = Object.assign({}, this.draft.photos);
      if (url) photos[stop.firstDay] = url;
      else delete photos[stop.firstDay];
      this.draft = Object.assign({}, this.draft, { photos });
    },
    setName(stop, name) {
      const names = Object.assign({}, this.draft.names);
      if (name.trim()) names[stop.firstDay] = name;
      else delete names[stop.firstDay];
      this.draft = Object.assign({}, this.draft, { names });
    },
    // The resized copy is not there for every upload.
    useFullImage(event, url) {
      if (event.target.src !== url) event.target.src = url;
    },
    resetToAutomatic() {
      const automatic = buildPosterStops(this.days);
      this.draft = Object.assign({}, this.draft, {
        splits: automatic.map(stop => stop.firstDay),
        splitsEdited: false,
        photos: {},
        names: {},
      });
    },
    async save() {
      this.saving = true;
      this.failed = false;
      const saved = await this.$store.dispatch('saveRidePresentation', {
        rideId: this.ride.id,
        design: this.draft.design,
        sections: { poster: this.draftSettings },
      });
      this.saving = false;
      if (saved) this.close();
      else this.failed = true;
    },
  },
};
</script>

<style scoped>
/* Sizes in px: the app's root font is 10px, so rem here would be tiny. */
.poster-editor {
  font-size: 15px;
  text-align: start;
}

.editor-section {
  margin-bottom: 26px;
}

.editor-title {
  margin: 0 0 6px;
  font-size: 18px;
  font-weight: 700;
  color: #1d1d1f;
}

.editor-hint {
  margin: 0 0 10px;
  font-size: 14px;
  color: #6e6e73;
}

.editor-designs {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.editor-design {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 150px;
  padding: 10px;
  border: 0;
  border-radius: 14px;
  background: #f6f6f8;
  box-shadow: inset 0 0 0 2px transparent;
  cursor: pointer;
}

.editor-design.is-picked {
  box-shadow: inset 0 0 0 2px var(--primary-color, #07A0C3);
  background: #fff;
}

.editor-design-name {
  font-size: 15px;
  font-weight: 600;
}

.editor-days {
  max-width: 480px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.editor-day {
  padding-inline-start: 12px;
  border-inline-start: 3px solid #e5e5ea;
}

.editor-day.starts-stop {
  margin-top: 10px;
  border-inline-start-color: var(--primary-color, #07A0C3);
}

.editor-day.starts-stop:first-child {
  margin-top: 0;
}

.editor-stop-tag {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: var(--primary-color, #07A0C3);
}

.editor-day-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  padding: 6px 0;
  cursor: pointer;
}

.editor-day-row input {
  width: 18px;
  height: 18px;
  accent-color: var(--primary-color, #07A0C3);
}

.editor-day-label {
  flex: 0 0 auto;
  font-weight: 700;
}

.editor-day-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  color: #6e6e73;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.editor-day-new {
  flex: 0 0 auto;
  font-size: 12px;
  color: #8e8e93;
}

.editor-stop {
  margin-bottom: 16px;
}

.editor-stop-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.editor-stop-number {
  flex: 0 0 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--primary-background, #07A0C3);
  color: var(--primary-text-color, #fff);
  font-size: 13px;
  font-weight: 700;
}

/* The stop's name, edited in place: looks like the name until focused. */
.editor-stop-name {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 260px;
  padding: 4px 10px;
  border: 1.5px solid #dcdce0;
  border-radius: 10px;
  background: #fff;
  color: #1d1d1f;
  font: inherit;
  font-size: 15px;
  font-weight: 700;
}

.editor-stop-name::placeholder {
  color: #1d1d1f;
  opacity: 0.55;
}

.editor-stop-name:focus {
  outline: none;
  border-color: var(--primary-color, #07A0C3);
}

.editor-stop-days {
  color: var(--primary-color, #07A0C3);
  font-size: 14px;
  font-weight: 600;
}

.editor-photos {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 3px 3px 6px;
}

.editor-photo {
  flex: 0 0 96px;
  height: 72px;
  padding: 0;
  overflow: hidden;
  border: 0;
  border-radius: 10px;
  background: #eef1f5;
  box-shadow: 0 0 0 2px transparent;
  cursor: pointer;
}

.editor-photo img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.editor-photo.is-picked {
  box-shadow: 0 0 0 3px var(--primary-color, #07A0C3);
}

.editor-photo-auto {
  position: relative;
}

.editor-photo-auto-label {
  position: absolute;
  inset: auto 0 0;
  padding: 3px 0;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  /* Centred with flex: the app's Hebrew rule forces text-align: right. */
  display: flex;
  justify-content: center;
}

/* Room for all of them on a wide screen; a swipeable row on a phone. */
@media (min-width: 768px) {
  .editor-photos {
    flex-wrap: wrap;
    overflow-x: visible;
  }
}

.editor-footer {
  position: sticky;
  bottom: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 0;
  border-top: 1px solid #e5e5ea;
  background: #fff;
  /* Covers the modal's bottom padding, so nothing scrolls by under it. */
  box-shadow: 0 16px 0 #fff;
}

.editor-footer-actions {
  display: flex;
  gap: 8px;
}

.editor-error {
  color: #c62828;
  font-size: 14px;
}

.editor-btn {
  padding: 8px 18px;
  border: 0;
  border-radius: 999px;
  background: #f2f2f4;
  color: #1d1d1f;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}

.editor-btn-quiet {
  background: none;
  color: var(--primary-color, #07A0C3);
  padding-inline: 0;
}

.editor-btn-primary {
  background: var(--primary-background, #07A0C3);
  color: var(--primary-text-color, #fff);
}

.editor-btn-primary:disabled {
  opacity: 0.6;
  cursor: default;
}
</style>

<style>
/* The modal's header is outside this component, so not scoped. */
.poster-editor-title {
  font-size: 20px;
  font-weight: 700;
}
</style>
